import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ChevronRight, ChevronLeft, Bot, Building2 } from 'lucide-react';
import PacerTaskDialog from './PacerTaskDialog';
import PacerSearch from './PacerSearch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00Z');
  if (isNaN(d)) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

function fmtMonth(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00Z');
  if (isNaN(d)) return '';
  return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' });
}

function statusPill(status) {
  const s = (status || '').toLowerCase();
  if (s.includes('complete')) return { bg: '#DCFCE7', fg: '#15803D', label: 'Done' };
  if (s.includes('progress')) return { bg: '#FEF3C7', fg: '#92400E', label: 'In progress' };
  if (s.includes('cancel') || s.includes('block')) return { bg: '#FEE2E2', fg: '#B91C1C', label: 'Blocked' };
  if (s.includes('hold')) return { bg: '#E0E7FF', fg: '#4338CA', label: 'On hold' };
  return { bg: '#E5E7EB', fg: '#374151', label: 'Not started' };
}

/**
 * Top-of-page tree row. Renders 15-20 AI-curated milestones as a
 * horizontal scrollable "tree" with a hairline spine + alternating
 * top/bottom branches. Click a node to drill into its rolled-up tasks.
 */
function PacerTree({ tree, onNodeClick }) {
  if (!tree || tree.length === 0) return null;
  return (
    <div data-testid="pacer-tree" className="relative w-full mb-8">
      {/* Spine */}
      <div
        className="absolute left-0 right-0 h-[2px]"
        style={{ top: '50%', background: 'linear-gradient(90deg,#0EA5E9 0%,#1F4FBE 50%,#7C3AED 100%)', opacity: 0.35 }}
      />
      <div className="relative flex items-stretch gap-3 overflow-x-auto pb-3 px-1" style={{ scrollbarWidth: 'thin' }}>
        {tree.map((node, i) => {
          const above = i % 2 === 0;
          return (
            <button
              key={node.id}
              type="button"
              onClick={() => onNodeClick(node)}
              className="flex-shrink-0 w-[220px] group focus:outline-none"
              data-testid={`pacer-tree-node-${i}`}
              style={{ marginTop: above ? 0 : '64px', marginBottom: above ? '64px' : 0 }}
            >
              <div
                className="rounded-2xl px-4 py-3 text-left shadow-sm transition-all group-hover:-translate-y-0.5 group-hover:shadow-md"
                style={{
                  backgroundColor: 'white',
                  borderTop: `4px solid ${node.accent_color || '#1F4FBE'}`,
                  border: '1px solid #E5E7EB',
                  borderTopWidth: '4px',
                  borderTopColor: node.accent_color || '#1F4FBE',
                }}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <span
                    className="text-[9px] font-bold tracking-[0.18em] uppercase"
                    style={{ color: node.accent_color || '#1F4FBE', fontFamily: 'Manrope' }}
                  >
                    M{i + 1} · {fmtMonth(node.target_date)}
                  </span>
                </div>
                <div
                  className="text-[13px] font-bold leading-snug mb-1"
                  style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}
                >
                  {node.label}
                </div>
                {node.kicker && (
                  <div
                    className="text-[11px] leading-snug italic line-clamp-2"
                    style={{ color: '#6B7280', fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    {node.kicker}
                  </div>
                )}
                <div className="mt-2 flex items-center justify-between text-[10px]" style={{ color: '#6B7280', fontFamily: 'Manrope' }}>
                  <span>{(node.departments || []).slice(0, 2).join(' · ') || '—'}</span>
                  <span>{(node.task_ids || []).length} tasks</span>
                </div>
              </div>
              {/* Vertical drop to spine */}
              <div
                className="mx-auto w-[2px]"
                style={{
                  height: '52px',
                  background: node.accent_color || '#1F4FBE',
                  opacity: 0.45,
                  marginTop: above ? '8px' : '-60px',
                  marginBottom: above ? '-60px' : '8px',
                }}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * One scrollable per-department strip. Shows the first ~14 tasks
 * (chronological). Click a task to open the detail dialog.
 */
function DepartmentStrip({ dept, accent, tasks, onTaskClick, showAll, onToggleShowAll }) {
  const [scroller, setScroller] = useState(null);
  const visible = showAll ? tasks : tasks.slice(0, 14);
  const scroll = (dir) => {
    if (!scroller) return;
    scroller.scrollBy({ left: dir * 320, behavior: 'smooth' });
  };
  return (
    <div className="mb-5" data-testid={`pacer-dept-${dept.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="w-2 h-6 rounded-sm" style={{ backgroundColor: accent }} />
          <h3 className="text-sm font-bold tracking-wide" style={{ fontFamily: 'Manrope', color: '#1A2E2E' }}>
            {dept}
          </h3>
          <span className="text-[11px]" style={{ fontFamily: 'Manrope', color: '#6B7280' }}>
            {tasks.length} tasks
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onToggleShowAll}
            className="text-[10px] font-bold px-2 py-1 rounded-md"
            style={{ fontFamily: 'Manrope', color: accent, border: `1px solid ${accent}33` }}
            data-testid={`pacer-dept-toggle-${dept.replace(/\s+/g, '-').toLowerCase()}`}
          >
            {showAll ? 'Show first 14' : `Show all ${tasks.length}`}
          </button>
          <button
            type="button"
            onClick={() => scroll(-1)}
            className="p-1 rounded-md text-gray-500 hover:bg-gray-100"
            aria-label="Scroll left"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => scroll(1)}
            className="p-1 rounded-md text-gray-500 hover:bg-gray-100"
            aria-label="Scroll right"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div
        ref={setScroller}
        className="flex items-stretch gap-2 overflow-x-auto pb-2"
        style={{ scrollbarWidth: 'thin' }}
      >
        {visible.map((t) => {
          const pill = statusPill(t.status);
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => onTaskClick(t)}
              className="flex-shrink-0 w-[300px] text-left rounded-xl p-3 transition-all hover:-translate-y-0.5 hover:shadow-md"
              style={{ backgroundColor: 'white', border: `1px solid ${accent}33`, borderLeft: `4px solid ${accent}` }}
              data-testid={`pacer-task-${t.id}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-bold tracking-wider" style={{ color: '#6B7280', fontFamily: 'Manrope' }}>
                  {fmtDate(t.target_date) || t.target_date_raw || 'TBD'}
                </span>
                <span
                  className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded"
                  style={{ backgroundColor: pill.bg, color: pill.fg, fontFamily: 'Manrope' }}
                >
                  {pill.label}
                </span>
              </div>
              <div
                className="text-[12px] font-semibold leading-snug line-clamp-2 mb-1"
                style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}
              >
                {t.task_name}
              </div>
              {t.responsible && (
                <div className="text-[10px]" style={{ color: '#6B7280', fontFamily: 'Manrope' }}>
                  Owner: {t.responsible.split(/[·,]/)[0].trim()}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function PacerRoadmap() {
  const [tree, setTree] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selected, setSelected] = useState(null);
  const [expandedDepts, setExpandedDepts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      axios.get(`${API}/pacer/tree`).catch(() => ({ data: { tree: [] } })),
      axios.get(`${API}/pacer/tasks`).catch(() => ({ data: { tasks: [] } })),
      axios.get(`${API}/pacer/departments`).catch(() => ({ data: { departments: [] } })),
    ]).then(([t, ta, d]) => {
      if (cancelled) return;
      setTree(t.data?.tree || []);
      setTasks(ta.data?.tasks || []);
      setDepartments(d.data?.departments || []);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const tasksByDept = useMemo(() => {
    const map = {};
    tasks.forEach((t) => {
      if (!map[t.department]) map[t.department] = [];
      map[t.department].push(t);
    });
    Object.values(map).forEach((arr) => arr.sort((a, b) => (a.target_date || '').localeCompare(b.target_date || '')));
    return map;
  }, [tasks]);

  if (loading) {
    return (
      <section className="py-16 text-center text-sm" style={{ color: '#6B7280', fontFamily: 'Manrope' }}>
        Loading Pacer roadmap…
      </section>
    );
  }
  if (!tree.length && !tasks.length) return null;

  return (
    <section
      data-testid="pacer-roadmap-section"
      className="py-14 px-4 sm:px-6 lg:px-10"
      style={{ background: 'linear-gradient(180deg,#F1F5FF 0%,#FAF8F3 100%)' }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-3" style={{ backgroundColor: '#1F4FBE', color: 'white' }}>
            <Bot className="w-3.5 h-3.5" />
            <span className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ fontFamily: 'Manrope' }}>
              Contractor · Pacer Roadmap
            </span>
          </div>
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl mb-2"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A2E2E', fontWeight: 500 }}
          >
            TLDI Master Pacer
          </h2>
          <p className="text-base italic" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#7A8C8C' }}>
            AI-curated top milestones above &middot; verbatim department task lists below
          </p>
          <p className="mt-2 text-[11px] italic" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
            {tasks.length} contractor tasks across {departments.filter((d) => d.task_count).length} departments
          </p>
        </div>

        {/* In-module search — iter-118 */}
        <PacerSearch
          tasks={tasks}
          departments={departments}
          onPickTask={(t, accent) => setSelected({ kind: 'task', task: t, accent })}
        />

        {/* AI Tree */}
        <PacerTree tree={tree} onNodeClick={(node) => setSelected({ kind: 'tree', node })} />

        {/* Departmental strips */}
        <div className="mt-10 mb-3 flex items-center gap-2">
          <Building2 className="w-4 h-4" style={{ color: '#1F4FBE' }} />
          <h3 className="text-sm font-bold tracking-[0.18em] uppercase" style={{ fontFamily: 'Manrope', color: '#1A2E2E' }}>
            Departmental detail · click any task
          </h3>
        </div>

        {departments
          .filter((d) => d.task_count > 0)
          .map((d) => (
            <DepartmentStrip
              key={d.department}
              dept={d.department}
              accent={d.accent_color}
              tasks={tasksByDept[d.department] || []}
              onTaskClick={(t) => setSelected({ kind: 'task', task: t, accent: d.accent_color })}
              showAll={!!expandedDepts[d.department]}
              onToggleShowAll={() =>
                setExpandedDepts((prev) => ({ ...prev, [d.department]: !prev[d.department] }))
              }
            />
          ))}
      </div>

      <PacerTaskDialog
        open={!!selected}
        onClose={() => setSelected(null)}
        payload={selected}
        allTasks={tasks}
      />
    </section>
  );
}
